/**
 * Contexto de un post + todos sus comentarios (cross-conversación).
 *
 * Alimenta el preview del post (cuenta, contador de sin responder) y la vista
 * "Ver todos los comentarios de un post" del inbox. Solo aplica a comentarios.
 */
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api";
import type { ApiIncomingMessage } from "@/lib/adapters";

export interface PostContext {
  postId: string;
  platform: string | null;
  accountHandle: string | null;
  totalComments: number;
  unansweredCount: number;
  /** Enriquecidos via Meta cuando estén disponibles; null por ahora. */
  permalink: string | null;
  caption: string | null;
  thumbnailUrl: string | null;
}

export interface PostThread {
  post: PostContext;
  comments: ApiIncomingMessage[];
}

export function usePostComments(
  clientSlug: string | undefined,
  postId: string | undefined | null,
) {
  return useQuery<PostThread>({
    queryKey: ["post-comments", clientSlug, postId],
    enabled: !!clientSlug && !!postId,
    queryFn: () =>
      apiGet<PostThread>(
        `/conversations/${clientSlug}/posts/${encodeURIComponent(postId!)}/comments`,
      ),
  });
}

// Contexto del post resuelto via Meta a partir del comment_id (caption, imagen,
// permalink) — funciona aunque no tengamos guardado el post id.
export interface CommentPost {
  commentId: string;
  platform: string;
  accountHandle: string | null;
  mediaId: string | null;
  caption: string | null;
  imageUrl: string | null;
  permalink: string | null;
}

export function useCommentPost(
  clientSlug: string | undefined,
  commentId: string | undefined | null,
) {
  return useQuery<{ post: CommentPost }>({
    queryKey: ["comment-post", clientSlug, commentId],
    enabled: !!clientSlug && !!commentId,
    staleTime: 5 * 60 * 1000, // el post no cambia; cacheamos 5 min.
    queryFn: () =>
      apiGet<{ post: CommentPost }>(
        `/conversations/${clientSlug}/comments/${encodeURIComponent(commentId!)}/post`,
      ),
  });
}
